import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { X, Settings, Users, Share2, Trash2, Save, Copy, Check, Upload, Image as ImageIcon, FileText, Mail, Sparkles, RefreshCw } from 'lucide-react'
import { Project } from '../../types'
import { useProjectStore } from '../../stores/projectStore'
import { useAuthStore } from '../../stores/authStore'
import { sendCollaboratorInvite } from '../../lib/firebase/invites'
import CharactersList from './CharactersList'

interface ProjectSettingsDialogProps {
  open: boolean
  onClose: () => void
  project: Project
  initialTab?: 'basic' | 'document' | 'sharing' | 'characters'
}

const aspectRatios = [
  { label: '16:9 (Widescreen)', value: '16:9' },
  { label: '4:3 (Standard)', value: '4:3' },
  { label: '4:5 (Portrait)', value: '4:5' },
  { label: '1:1 (Square)', value: '1:1' },
  { label: '9:16 (Vertical)', value: '9:16' },
  { label: '21:9 (Ultrawide)', value: '21:9' },
  { label: '3:2 (Photography)', value: '3:2' }
]

const ProjectSettingsDialog: React.FC<ProjectSettingsDialogProps> = ({ 
  open, 
  onClose, 
  project,
  initialTab = 'basic'
}) => {
  const [activeTab, setActiveTab] = useState<'basic' | 'document' | 'sharing' | 'characters'>(initialTab)
  const [title, setTitle] = useState(project.title)
  const [description, setDescription] = useState(project.description)
  const [aspectRatio, setAspectRatio] = useState(project.aspectRatio)
  const [isPublic, setIsPublic] = useState(project.isPublic || false)
  const [allowComments, setAllowComments] = useState(project.allowComments !== false)
  const [collaboratorEmail, setCollaboratorEmail] = useState('')
  const [inspirationalText, setInspirationalText] = useState(project.inspirationalText || '')
  const [inspirationalImage, setInspirationalImage] = useState<string | null>(project.inspirationalImage || null)
  const [copiedLink, setCopiedLink] = useState(false)
  const [sendingInvite, setSendingInvite] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved'>('idle')
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)
  const [regeneratingThumbnails, setRegeneratingThumbnails] = useState(false)
  const [thumbnailsResult, setThumbnailsResult] = useState<{ success: number; failed: number; skipped: number } | null>(null)
  
  const { updateProject, updateProjectSettings, deleteProject, regenerateThumbnails, loading } = useProjectStore()
  const { user } = useAuthStore()

  // Generate shareable link
  const shareableLink = project.shareableLink || `${window.location.origin}/view/${project.id}`

  useEffect(() => {
    if (open) {
      setTitle(project.title)
      setDescription(project.description)
      setAspectRatio(project.aspectRatio)
      setIsPublic(project.isPublic || false)
      setAllowComments(project.allowComments !== false)
      setInspirationalText(project.inspirationalText || '')
      setInspirationalImage(project.inspirationalImage || null)
      setActiveTab(initialTab)
    }
  }, [open, project, initialTab])

  // Handle dialog close with unsaved changes warning
  const handleClose = () => {
    if (hasUnsavedChanges && saveStatus !== 'saved') {
      const confirmed = window.confirm('You have unsaved changes. Are you sure you want to close?')
      if (!confirmed) return
    }
    onClose()
  }

  // Handle Esc key to close dialog
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && open && !showDeleteConfirm) {
        handleClose()
      }
    }
    
    window.addEventListener('keydown', handleEsc)
    return () => window.removeEventListener('keydown', handleEsc)
  }, [open, showDeleteConfirm, hasUnsavedChanges, saveStatus])

  // Track changes
  useEffect(() => {
    if (!open) return

    const hasChanges = 
      title.trim() !== project.title ||
      description.trim() !== project.description ||
      aspectRatio !== project.aspectRatio ||
      isPublic !== (project.isPublic || false) ||
      allowComments !== (project.allowComments !== false) ||
      inspirationalText.trim() !== (project.inspirationalText || '') ||
      inspirationalImage !== project.inspirationalImage

    setHasUnsavedChanges(hasChanges)
    
    if (hasChanges) {
      setSaveStatus('idle')
    }
  }, [title, description, aspectRatio, isPublic, allowComments, inspirationalText, inspirationalImage, open, project])

  // Autosave functionality
  useEffect(() => {
    if (!open || !hasUnsavedChanges) return

    const autoSave = async () => {
      try {
        setSaveStatus('saving')
        console.log('Saving project settings:', { isPublic, allowComments, projectId: project.id })
        
        // Generate shareable link if project is public
        const linkToSave = isPublic 
          ? (project.shareableLink || `${window.location.origin}/view/${project.id}`)
          : null

        // Build updates object, excluding undefined values
        const updates: Record<string, any> = {
          title: title.trim() || project.title,
          description: description.trim(),
          aspectRatio,
          isPublic,
          allowComments,
          inspirationalText: inspirationalText.trim()
        }

        // Only include optional fields if they have values
        if (linkToSave) {
          updates.shareableLink = linkToSave
        }
        
        if (inspirationalImage) {
          updates.inspirationalImage = inspirationalImage
        }

        console.log('Update payload:', updates)

        // Update the project in Firebase
        await updateProject(project.id, updates)

        console.log('Project updated successfully')
        
        setSaveStatus('saved')
        setHasUnsavedChanges(false)
        
        // Reset to idle after 2 seconds
        setTimeout(() => setSaveStatus('idle'), 2000)
      } catch (error) {
        console.error('Autosave failed:', error)
        alert('Failed to save settings. Please try again.')
        setSaveStatus('idle')
      }
    }

    // Debounce autosave by 1 second
    const timeoutId = setTimeout(autoSave, 1000)
    return () => clearTimeout(timeoutId)
  }, [title, description, aspectRatio, isPublic, allowComments, inspirationalText, inspirationalImage, open, hasUnsavedChanges, project, updateProject])


  const handleDeleteProject = async () => {
    try {
      await deleteProject(project.id)
      setShowDeleteConfirm(false)
      onClose()
    } catch (error) {
      console.error('Failed to delete project:', error)
      alert('Failed to delete project. Please try again.')
    }
  }

  const handleAddCollaborator = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!collaboratorEmail.trim() || !user?.email) return

    setSendingInvite(true)
    try {
      await sendCollaboratorInvite(
        project.id,
        project.title,
        user.email,
        collaboratorEmail.trim(),
        'editor'
      )
      
      setCollaboratorEmail('')
      alert(`Invitation sent to ${collaboratorEmail}`)
    } catch (error) {
      console.error('Failed to send invite:', error)
      alert('Failed to send invitation. Please try again.')
    } finally {
      setSendingInvite(false)
    }
  }

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareableLink)
      setCopiedLink(true)
      setTimeout(() => setCopiedLink(false), 2000)
    } catch (error) {
      console.error('Failed to copy link:', error)
    }
  }

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      const reader = new FileReader()
      reader.onload = (e) => {
        setInspirationalImage(e.target?.result as string)
      }
      reader.readAsDataURL(file)
    }
  }

  const handleRegenerateThumbnails = async () => {
    if (regeneratingThumbnails) return
    
    const confirmed = window.confirm(
      'This will regenerate thumbnails for all images in this project that don\'t have thumbnails yet. This may take a few minutes. Continue?'
    )
    
    if (!confirmed) return
    
    setRegeneratingThumbnails(true)
    setThumbnailsResult(null)
    
    try {
      const result = await regenerateThumbnails(project.id)
      
      if (result.success && result.processed) {
        setThumbnailsResult(result.processed)
        
        // Show success message
        if (result.processed.success > 0) {
          alert(`Successfully regenerated ${result.processed.success} thumbnails!`)
        } else if (result.processed.skipped > 0) {
          alert(`All images already have thumbnails. ${result.processed.skipped} skipped.`)
        } else {
          alert('No images found to process.')
        }
      } else {
        alert(`Failed to regenerate thumbnails: ${result.error || 'Unknown error'}`)
      }
    } catch (error) {
      console.error('Error regenerating thumbnails:', error)
      alert('Failed to regenerate thumbnails. Please try again.')
    } finally {
      setRegeneratingThumbnails(false)
    }
  }

  if (!open) return null

  return (
    <>
      <AnimatePresence>
        <motion.div
        className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={handleClose}
      >
        <motion.div
          className="glass-card w-full max-w-4xl max-h-[90vh] overflow-y-auto border border-white/20"
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          transition={{ duration: 0.2 }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-white/10">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-primary-500/20 rounded-full flex items-center justify-center">
                <Settings className="w-5 h-5 text-primary-300" />
              </div>
              <h2 className="text-xl font-semibold text-white">Project Settings</h2>
            </div>
            <button
              onClick={handleClose}
              className="glass-button p-2 hover:bg-white/20"
            >
              <X className="w-5 h-5 text-white" />
            </button>
          </div>

          {/* Tabs */}
          <div className="border-b border-white/10 px-6">
            <div className="flex space-x-1">
              <button
                onClick={() => setActiveTab('basic')}
                className={`px-4 py-3 font-medium transition-colors border-b-2 ${
                  activeTab === 'basic'
                    ? 'border-primary-500 text-white'
                    : 'border-transparent text-white/60 hover:text-white/80'
                }`}
              >
                Basic Info
              </button>
              <button
                onClick={() => setActiveTab('document')}
                className={`px-4 py-3 font-medium transition-colors border-b-2 ${
                  activeTab === 'document'
                    ? 'border-primary-500 text-white'
                    : 'border-transparent text-white/60 hover:text-white/80'
                }`}
              >
                <div className="flex items-center space-x-2">
                  <FileText className="w-4 h-4" />
                  <span>Document</span>
                </div>
              </button>
              <button
                onClick={() => setActiveTab('sharing')}
                className={`px-4 py-3 font-medium transition-colors border-b-2 ${
                  activeTab === 'sharing'
                    ? 'border-primary-500 text-white'
                    : 'border-transparent text-white/60 hover:text-white/80'
                }`}
              >
                <div className="flex items-center space-x-2">
                  <Share2 className="w-4 h-4" />
                  <span>Sharing</span>
                </div>
              </button>
              <button
                onClick={() => setActiveTab('characters')}
                className={`px-4 py-3 font-medium transition-colors border-b-2 ${
                  activeTab === 'characters'
                    ? 'border-pink-500 text-white'
                    : 'border-transparent text-white/60 hover:text-white/80'
                }`}
              >
                <div className="flex items-center space-x-2">
                  <Sparkles className="w-4 h-4" />
                  <span>Characters</span>
                </div>
              </button>
            </div>
          </div>

          <div className="p-6 space-y-6">
            {/* Basic Info Tab */}
            {activeTab === 'basic' && (
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-white mb-2">
                    Project Title *
                  </label>
                  <input
                    type="text"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    className="glass-input w-full"
                    maxLength={100}
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-white mb-2">
                    Description
                  </label>
                  <textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    className="glass-input w-full h-32 resize-none"
                    maxLength={500}
                    placeholder="Describe your project..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-white mb-2">
                    Aspect Ratio
                  </label>
                  <select
                    value={aspectRatio}
                    onChange={(e) => setAspectRatio(e.target.value)}
                    className="glass-input w-full"
                  >
                    {aspectRatios.map(ratio => (
                      <option key={ratio.value} value={ratio.value} className="bg-gray-800 text-white">
                        {ratio.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Project Stats */}
                <div>
                  <h3 className="text-lg font-medium text-white mb-4">Project Statistics</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="glass-card p-4 text-center">
                      <div className="text-2xl font-semibold text-white mb-1">{project.totalScenes}</div>
                      <div className="text-sm text-white/70">Scenes</div>
                    </div>
                    <div className="glass-card p-4 text-center">
                      <div className="text-2xl font-semibold text-white mb-1">{project.totalImages}</div>
                      <div className="text-sm text-white/70">Images</div>
                    </div>
                    <div className="glass-card p-4 text-center">
                      <div className="text-2xl font-semibold text-white mb-1">{project.sharedWith.length}</div>
                      <div className="text-sm text-white/70">Collaborators</div>
                    </div>
                  </div>
                </div>

                {/* Image Optimization */}
                <div>
                  <h3 className="text-lg font-medium text-white mb-4">Image Optimization</h3>
                  <div className="glass-card p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1 mr-4">
                        <h4 className="text-white font-medium mb-1">Regenerate Thumbnails</h4>
                        <p className="text-white/70 text-sm">
                          Create optimized thumbnail images for faster loading. This will process all images that don't already have thumbnails.
                        </p>
                        {thumbnailsResult && (
                          <div className="mt-2 text-sm text-white/60">
                            Last run: {thumbnailsResult.success} created, {thumbnailsResult.skipped} skipped, {thumbnailsResult.failed} failed
                          </div>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={handleRegenerateThumbnails}
                        disabled={regeneratingThumbnails || loading}
                        className="glass-button bg-blue-500/30 hover:bg-blue-500/40 text-white px-4 py-2 flex items-center space-x-2 disabled:opacity-50"
                      >
                        <RefreshCw className={`w-4 h-4 ${regeneratingThumbnails ? 'animate-spin' : ''}`} />
                        <span>{regeneratingThumbnails ? 'Processing...' : 'Regenerate'}</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Document Tab */}
            {activeTab === 'document' && (
              <div className="space-y-6">
                {/* Inspirational Image */}
                <div>
                  <h3 className="text-lg font-medium text-white mb-4">Inspirational Image</h3>
                  <div className="glass-card p-4">
                    {inspirationalImage ? (
                      <div className="relative">
                        <img
                          src={inspirationalImage}
                          alt="Inspirational"
                          className="w-full h-64 object-cover rounded-lg"
                        />
                        <button
                          type="button"
                          onClick={() => setInspirationalImage(null)}
                          className="absolute top-2 right-2 glass-button p-2 hover:bg-red-500/20 text-red-300"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <div className="border-2 border-dashed border-white/20 rounded-lg p-8 text-center">
                        <Upload className="w-12 h-12 text-primary-400 mx-auto mb-4" />
                        <p className="text-white/70 text-sm mb-4">
                          Upload an inspirational image for your project
                        </p>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleImageUpload}
                          className="hidden"
                          id="inspirational-image"
                        />
                        <label
                          htmlFor="inspirational-image"
                          className="glass-button bg-primary-500/30 hover:bg-primary-500/40 text-white px-4 py-2 cursor-pointer inline-block"
                        >
                          Choose Image
                        </label>
                      </div>
                    )}
                  </div>
                </div>

                {/* Inspirational Text */}
                <div>
                  <h3 className="text-lg font-medium text-white mb-4">Inspirational Notes</h3>
                  <textarea
                    value={inspirationalText}
                    onChange={(e) => setInspirationalText(e.target.value)}
                    className="glass-input w-full h-40 resize-none"
                    placeholder="Add your inspirational notes, ideas, or references..."
                  />
                </div>
              </div>
            )}

            {/* Sharing Tab */}
            {activeTab === 'sharing' && (
              <div className="space-y-6">
                {/* Privacy & Sharing */}
                <div>
                  <h3 className="text-lg font-medium text-white mb-4">Privacy & Sharing</h3>
                  
                  <div className="space-y-4">
                    <div className="flex items-center justify-between glass-card p-4">
                      <div>
                        <h4 className="text-white font-medium">Public Project</h4>
                        <p className="text-white/70 text-sm">
                          Allow anyone with the link to view this project
                        </p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={isPublic}
                          onChange={(e) => setIsPublic(e.target.checked)}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-white/20 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-500"></div>
                      </label>
                    </div>

                    {/* Shareable Link - Only show when public */}
                    {isPublic && (
                      <div className="glass-card p-4">
                        <h4 className="text-white font-medium mb-3">Shareable Link</h4>
                        <div className="flex items-center space-x-2">
                          <input
                            type="text"
                            value={shareableLink}
                            readOnly
                            className="glass-input flex-1 text-sm"
                          />
                          <button
                            type="button"
                            onClick={handleCopyLink}
                            className="glass-button bg-primary-500/30 hover:bg-primary-500/40 text-white px-4 py-2 flex items-center space-x-2"
                          >
                            {copiedLink ? (
                              <>
                                <Check className="w-4 h-4" />
                                <span>Copied!</span>
                              </>
                            ) : (
                              <>
                                <Copy className="w-4 h-4" />
                                <span>Copy</span>
                              </>
                            )}
                          </button>
                        </div>
                        <p className="text-white/60 text-xs mt-2">
                          Anyone with this link can view your project and leave comments (if enabled)
                        </p>
                      </div>
                    )}

                    <div className="flex items-center justify-between glass-card p-4">
                      <div>
                        <h4 className="text-white font-medium">Allow Comments</h4>
                        <p className="text-white/70 text-sm">
                          Let viewers add comments to images
                        </p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={allowComments}
                          onChange={(e) => setAllowComments(e.target.checked)}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-white/20 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-500"></div>
                      </label>
                    </div>
                  </div>
                </div>

                {/* Collaborators */}
                <div>
                  <h3 className="text-lg font-medium text-white mb-4 flex items-center space-x-2">
                    <Users className="w-5 h-5" />
                    <span>Collaborators</span>
                  </h3>
                  
                  <div className="space-y-4">
                    <div className="glass-card p-4">
                      <p className="text-white/70 text-sm mb-3">
                        Invite registered users to collaborate on this project. They will receive an email invitation and can edit the project.
                      </p>
                      <div className="flex space-x-2">
                        <input
                          type="email"
                          value={collaboratorEmail}
                          onChange={(e) => setCollaboratorEmail(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && collaboratorEmail.trim() && !sendingInvite) {
                              handleAddCollaborator(e as any)
                            }
                          }}
                          placeholder="Enter email address..."
                          className="glass-input flex-1"
                        />
                        <button
                          type="button"
                          onClick={handleAddCollaborator}
                          className="glass-button bg-primary-500/30 hover:bg-primary-500/40 text-white px-4 py-2 flex items-center space-x-2 disabled:opacity-50"
                          disabled={!collaboratorEmail.trim() || sendingInvite}
                        >
                          <Mail className="w-4 h-4" />
                          <span>{sendingInvite ? 'Sending...' : 'Send Invite'}</span>
                        </button>
                      </div>
                    </div>

                    {/* Current Collaborators */}
                    {project.sharedWith && project.sharedWith.length > 0 && (
                      <div className="glass-card p-4">
                        <h4 className="text-white font-medium mb-3">Current Collaborators</h4>
                        <div className="space-y-2">
                          {project.sharedWith.map(user => (
                            <div key={user.id} className="flex items-center justify-between p-2 rounded bg-white/5">
                              <div>
                                <p className="text-white text-sm">{user.email}</p>
                                <p className="text-white/60 text-xs">{user.role}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Characters Tab */}
            {activeTab === 'characters' && (
              <div className="space-y-6">
                <div className="glass-card bg-gradient-to-r from-pink-500/10 to-purple-500/10 border-pink-500/20 p-4 rounded-lg">
                  <div className="flex items-start space-x-3">
                    <Sparkles className="w-6 h-6 text-pink-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <h4 className="text-white font-medium mb-1">AI Character Training</h4>
                      <p className="text-white/70 text-sm">
                        Train custom AI characters from your own images. Once trained, use them in the Generation dialog 
                        to create consistent character images across your project scenes.
                      </p>
                    </div>
                  </div>
                </div>
                
                <CharactersList projectId={project.id} />
              </div>
            )}

            {/* Action Buttons */}
              <div className="flex justify-between pt-6 border-t border-white/10">
                <button
                  type="button"
                  onClick={() => setShowDeleteConfirm(true)}
                  className="glass-button bg-red-500/30 hover:bg-red-500/40 text-red-200 px-4 py-2 flex items-center space-x-2"
                  disabled={loading}
                >
                  <Trash2 className="w-4 h-4" />
                  <span>Delete Project</span>
                </button>

                <div className="flex items-center space-x-3">
                  {/* Save Status Indicator */}
                  {saveStatus === 'saving' && (
                    <div className="flex items-center space-x-2 text-white/70 text-sm">
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      <span>Saving...</span>
                    </div>
                  )}
                  {saveStatus === 'saved' && (
                    <div className="flex items-center space-x-2 text-green-400 text-sm">
                      <Check className="w-4 h-4" />
                      <span>Saved</span>
                    </div>
                  )}
                  {saveStatus === 'idle' && !hasUnsavedChanges && (
                    <span className="text-white/50 text-sm">All changes saved</span>
                  )}
                  {saveStatus === 'idle' && hasUnsavedChanges && (
                    <span className="text-yellow-400/70 text-sm">Unsaved changes</span>
                  )}
                  <button
                    type="button"
                    onClick={handleClose}
                    className="glass-button text-white/90 hover:bg-white/10 px-6 py-2"
                  >
                    Close
                  </button>
                </div>
              </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>

      {/* Delete Confirmation Modal */}
      <AnimatePresence>
        {showDeleteConfirm && (
          <motion.div
            className="fixed inset-0 bg-black/70 backdrop-blur-md flex items-center justify-center z-[60] p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowDeleteConfirm(false)}
          >
            <motion.div
              className="glass-card w-full max-w-md border border-red-500/30 shadow-2xl overflow-hidden"
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header with gradient */}
              <div className="bg-gradient-to-r from-red-500/20 to-red-600/20 border-b border-red-500/30 p-6">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-red-500/30 rounded-full flex items-center justify-center ring-2 ring-red-500/50">
                    <Trash2 className="w-6 h-6 text-red-300" />
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold text-white">Delete Project</h2>
                    <p className="text-red-200/70 text-sm">This action cannot be undone</p>
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="p-6">
                <p className="text-white/80 leading-relaxed mb-2">
                  Are you sure you want to delete <span className="font-semibold text-white">"{project.title}"</span>?
                </p>
                <p className="text-white/60 text-sm mb-4">
                  All scenes, images, and data will be permanently deleted.
                </p>
                
                {/* Stats */}
                <div className="glass-card p-3 space-y-1 mb-4">
                  <div className="flex justify-between text-sm">
                    <span className="text-white/70">Scenes:</span>
                    <span className="text-white font-medium">{project.totalScenes}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-white/70">Images:</span>
                    <span className="text-white font-medium">{project.totalImages}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-white/70">Collaborators:</span>
                    <span className="text-white font-medium">{project.sharedWith.length}</span>
                  </div>
                </div>

                <p className="text-red-300 text-sm font-medium">
                  ⚠️ This will delete everything permanently!
                </p>
              </div>

              {/* Actions */}
              <div className="flex space-x-3 p-6 pt-0">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="flex-1 glass-button bg-white/5 hover:bg-white/10 text-white px-4 py-2.5 font-medium transition-all"
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteProject}
                  className="flex-1 glass-button bg-red-500/40 hover:bg-red-500/50 text-white px-4 py-2.5 font-medium transition-all shadow-lg shadow-red-500/20 disabled:opacity-50"
                  disabled={loading}
                >
                  {loading ? 'Deleting...' : 'Delete Project'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  )
}

export default ProjectSettingsDialog
